import { SwitchRow } from '@components/SwitchRow/SwitchRow';
import formCss from '@lib/style/form.module.scss';
import { FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import classNames from 'classnames';
import { useState } from 'react';
import type { ComposeFieldsProps } from '../../types';
import {
  canPublish,
  offeredPrivacyLevels,
  TIKTOK_PRIVACY_LABELS,
  TIKTOK_TITLE_MAX_LENGTH,
  type TikTokAccountCapabilities,
  type TikTokInteraction,
  type TikTokPostContent,
  type TikTokPrivacy,
} from './index';

const privacyLabel = (level: string): string =>
  Object.prototype.hasOwnProperty.call(TIKTOK_PRIVACY_LABELS, level)
    ? TIKTOK_PRIVACY_LABELS[level as TikTokPrivacy]
    : level;

/** The three interaction switches, in the account's `disabledInteractions` vocabulary. */
const INTERACTIONS: { id: TikTokInteraction; label: string; key: keyof TikTokPostContent }[] = [
  { id: 'comment', label: 'Allow comments', key: 'disableComment' },
  { id: 'duet', label: 'Allow duets', key: 'disableDuet' },
  { id: 'stitch', label: 'Allow stitches', key: 'disableStitch' },
];

/**
 * TikTok's part of the compose form: the inbox switch, then the caption,
 * privacy, interaction and disclosure fields of a published post. Switching
 * to the inbox drops every other field, as the API requires.
 */
export function TikTokComposeFields({
  content,
  onChange,
  capabilities,
  disabled,
}: ComposeFieldsProps<TikTokPostContent, TikTokAccountCapabilities>) {
  const levels = offeredPrivacyLevels(capabilities);
  // The gate's rule too: no publishable level leaves only the inbox.
  const inboxOnly = !canPublish(capabilities);
  // What the user had typed before switching to the inbox, restored when switching back:
  // the inbox variant carries nothing, so the fields live here meanwhile.
  const [kept, setKept] = useState<TikTokPostContent | null>(null);
  const disabledInteractions = capabilities?.disabledInteractions ?? [];

  const set = (patch: Partial<TikTokPostContent>) => onChange({ ...content, ...patch });

  return (
    <div className={formCss.composeFields}>
      <SwitchRow
        label="Send to TikTok inbox"
        hint={
          inboxOnly
            ? 'This account cannot publish through the API, so the media can only go to its inbox.'
            : 'The media lands in the account’s inbox to finish and post from the TikTok app. Nothing else is sent with it.'
        }
        checked={content.draft}
        disabled={disabled}
        onChange={(draft) => {
          if (draft) {
            setKept(content);
            onChange({ platform: 'tiktok', draft: true });
          } else {
            onChange({ ...(kept ?? content), draft: false });
          }
        }}
      />

      {!content.draft && (
        <>
          <div className={formCss.field}>
            <span className={formCss.fieldLabel}>Caption</span>
            <TextField
              className={formCss.textInput}
              multiline
              minRows={3}
              placeholder="Say something about this post #hashtags @mentions"
              value={content.title ?? ''}
              disabled={disabled}
              onChange={(e) => set({ title: e.target.value || undefined })}
              helperText={`${(content.title ?? '').length.toLocaleString()} / ${TIKTOK_TITLE_MAX_LENGTH.toLocaleString()}`}
              slotProps={{ htmlInput: { maxLength: TIKTOK_TITLE_MAX_LENGTH } }}
            />
          </div>

          <div className={formCss.field}>
            <span className={formCss.fieldLabel}>Who can see this post</span>
            <RadioGroup
              className={classNames(formCss.radioGroup, formCss.radioRow)}
              value={content.privacy ?? ''}
              onChange={(e) => set({ privacy: e.target.value as TikTokPrivacy })}
            >
              {levels.map((level) => (
                <FormControlLabel
                  key={level}
                  value={level}
                  control={<Radio />}
                  label={privacyLabel(level)}
                  disabled={disabled}
                />
              ))}
            </RadioGroup>
          </div>

          <div className={formCss.switchStack}>
            {INTERACTIONS.map(({ id, label, key }) => {
              const off = disabledInteractions.includes(id);
              return (
                <SwitchRow
                  key={id}
                  label={label}
                  hint={off ? 'Turned off on the account' : undefined}
                  checked={!off && content[key] !== true}
                  disabled={disabled || off}
                  onChange={(allowed) => set({ [key]: !allowed })}
                />
              );
            })}
          </div>

          <div className={formCss.field}>
            <span className={formCss.fieldLabel}>Disclosures</span>
            <div className={formCss.switchStack}>
              <SwitchRow
                label="AI-generated content"
                hint="Adds TikTok’s AI label to the post."
                checked={content.aiGenerated === true}
                disabled={disabled}
                onChange={(aiGenerated) => set({ aiGenerated })}
              />
              <SwitchRow
                label="Paid partnership"
                hint="The post promotes a third party in exchange for something of value."
                checked={content.paidPartnership === true}
                disabled={disabled}
                onChange={(paidPartnership) => set({ paidPartnership })}
              />
              <SwitchRow
                label="Promotes your own brand"
                hint="The post promotes your own business, product or service."
                checked={content.promotesOwnBrand === true}
                disabled={disabled}
                onChange={(promotesOwnBrand) => set({ promotesOwnBrand })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
